/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, DoCheck, HostListener, Input, QueryList, ViewChildren, ViewContainerRef } from '@angular/core';
import { AbstractObject } from '@src/app/model/abstract-object';
import { Field } from '@src/app/shared/field/Field';
import { AppFormControl, ControlOn } from '@src/app/shared/form/app-form-control';
import { FormControlInterface } from '@src/app/shared/form/form-control-interface';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormErrorComponent } from '@src/app/shared/form/form-error/form-error.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepicker, MatTimepickerModule } from '@angular/material/timepicker';
import { IRISH_DATE_PROVIDERS, typedDateWasRejected } from '@src/app/shared/form/irish-date-adapter';

const FULL_DATE_AND_TIME: Intl.DateTimeFormatOptions = {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZoneName: 'short',
};

const MIDDAY = 12;

const TIME_ZONE_NAME_ONLY = new Intl.DateTimeFormat('en-IE', { timeZoneName: 'short' });

const SECONDS_PER_UNIT: [string, number][] = [
    ['year', 365 * 24 * 60 * 60],
    ['month', 30 * 24 * 60 * 60],
    ['day', 24 * 60 * 60],
    ['hour', 60 * 60],
    ['minute', 60],
];

function isValidDate(value: unknown): value is Date {
    return value instanceof Date && !isNaN(value.getTime());
}

function describeRelativeToNow(pointInTime: Date): string {
    const secondsFromNow = (pointInTime.getTime() - Date.now()) / 1000;
    const distanceInSeconds = Math.abs(secondsFromNow);
    const largestFittingUnit = SECONDS_PER_UNIT.find(([, seconds]) => distanceInSeconds >= seconds);
    if (!largestFittingUnit) {
        return $localize`just now`;
    }
    const [unitName, secondsInUnit] = largestFittingUnit;
    const count = Math.round(distanceInSeconds / secondsInUnit);
    const distance = `${count} ${unitName}${count === 1 ? '' : 's'}`;
    return secondsFromNow < 0 ? $localize`${distance} ago` : $localize`in ${distance}`;
}

class AppDateControl extends AppFormControl {
    field: DateTimeField
}

export class DateTimeField extends Field {
    constructor(defaultOptions: Partial<DateTimeField>, moreOptions: Partial<DateTimeField> = {}) {
        super(defaultOptions, moreOptions);
        if (moreOptions.cellOpts) {
            this.cellOpts = { ...this.cellOpts, ...moreOptions.cellOpts };
        }
    }
    formatValue(value) {
        return value ? describeRelativeToNow(new Date(value * 1000)) : '';
    }
    asDate(o: AbstractObject) {
        if (o) {
            const val = this.getValue(o);
            if (val) {
                return new Date(val * 1000);
            }
        }
        return null;
    }

    public getFormValue() {
        const selected: Date | null = this.control.value;
        return isValidDate(selected) ? Math.floor(selected.getTime() / 1000) : null;
    }

    setValue(item: AbstractObject, readonly: boolean) {
        if (readonly) {
            this.readonly = true;
        } else {
            this.readonly = this.defaultReadonly;
        }
        const unixSeconds = this.getValue(item);
        const selected = unixSeconds > 0 ? new Date(unixSeconds * 1000) : null;
        this.control.setValue(selected, { emitEvent: false });
        this.control.markAsPristine();
        this.control.markAsUntouched();
    }

    toLongDate(o: AbstractObject) {
        if (o) {
            const val = this.getValue(o);
            if (val) {
                const theDate = new Date(val * 1000);
                return (
                    theDate.toLocaleDateString(undefined,
                        { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
                    ) + ' ' + theDate.toLocaleTimeString([], { hour12: true, hour: 'numeric', minute: '2-digit' })
                );
            } else {
                return '';
            }
        } else {
            return '';
        }

    }

    setToCurrentDateTime(emitEvent = false) {
        const startOfCurrentMinute = new Date();
        startOfCurrentMinute.setSeconds(0, 0);
        this.control.setValue(startOfCurrentMinute, { emitEvent });
    }
}


@Component({
    selector: 'app-form-date-time',
    templateUrl: './form-date-time.component.html',
    styleUrls: ['./form-date-time.component.scss'],
    imports: [MatFormFieldModule, MatInputModule, FormsModule, ReactiveFormsModule,
        FormErrorComponent, MatTooltipModule, MatIconModule, MatDatepickerModule, MatTimepickerModule],
    providers: IRISH_DATE_PROVIDERS
})
export class FormDateTimeComponent implements FormControlInterface, DoCheck {

    @Input() control: AppDateControl;
    @Input() on: ControlOn = 'form';

    relativeToNowText = '';

    @ViewChildren(MatTimepicker) timePickers: QueryList<MatTimepicker<Date>>;

    /** The time picker closes on outside clicks, but a click inside an iframe (e.g. TinyMCE)
     *  never reaches this page - it only blurs the window. */
    @HostListener('window:blur')
    closeTimePickersWhenFocusLeavesThePage() {
        this.timePickers?.forEach(timePicker => timePicker.close());
    }

    public static make(heading: string, value: string, options: Partial<DateTimeField> = {}) {
        const field: Field = new DateTimeField({ cellOpts: { heading }, value, type: 'dateTime' }, options);
        field.formControlFactory = FormDateTimeComponent.createComponent;
        return field;
    }

    public static createComponent(vcr: ViewContainerRef, ctl: AppDateControl, on: ControlOn) {
        const componentRef = vcr.createComponent(FormDateTimeComponent);
        componentRef.instance.control = ctl;
        componentRef.instance.on = on;
        return componentRef;
    }

    /** Read the clock in ngDoCheck, not from the template: dev mode's checkNoChanges pass skips
     *  lifecycle hooks, so both passes see the same text even when a minute rolls over between them. */
    ngDoCheck() {
        const selected: Date | null = this.control.value;
        this.relativeToNowText = isValidDate(selected) ? describeRelativeToNow(selected) : '';
    }

    public getCurrentFullDate() {
        const selected: Date | null = this.control.value;
        return isValidDate(selected) ? selected.toLocaleString('en-IE', FULL_DATE_AND_TIME) : '';
    }

    public getTimeZoneName() {
        const selected: Date | null = this.control.value;
        const zoneFor = isValidDate(selected) ? selected : new Date();
        return TIME_ZONE_NAME_ONLY.formatToParts(zoneFor).find(part => part.type === 'timeZoneName')?.value ?? '';
    }

    public isEditable() {
        return !this.control.field.readonly && !this.control.field.disable && !this.control.disabled;
    }

    public applyChosenDateKeepingTimeOfDay(change: MatDatepickerInputEvent<Date>) {
        const previous: Date | null = this.control.value;
        if (typedDateWasRejected(change)) {
            change.target.value = previous;
            return;
        }
        const chosenDate = change.value;
        let combined: Date | null = null;
        if (isValidDate(chosenDate)) {
            combined = new Date(chosenDate.getTime());
            combined.setHours(
                isValidDate(previous) ? previous.getHours() : MIDDAY,
                isValidDate(previous) ? previous.getMinutes() : 0,
                0, 0);
        }
        this.control.setValue(combined);
        this.control.markAsDirty();
        this.control.markAsTouched();
    }
}
